import React, { useState } from 'react';
import { useAppointmentData } from 'hooks/useAppointmentData';
import { useXrayData } from 'hooks/useXrayData';
import { containerStyles, getActiveExamTypesFromAppointment } from 'helpers/helpers';
import { ExamNameNormalizer, ExamTypes, ViewTypes, Xray } from 'models/XrayModels';
import { useAuth } from 'hooks/useAuth';
import MDBox from 'components/atoms/MDBox/MDBox';
import { useXrayMutations } from 'hooks/useXrayMutations';
import { sendXraysForAnalysis } from 'services/mlService';
import { Card, Container, Grid, Modal, Typography } from '@mui/material';
import MDAppBar from 'components/organisms/MDAppBar/MDAppBar';
import { Breakpoints, MUIBaseColors, MUIColors } from 'models/StyleModels';
import { useBreakpoints } from 'hooks/useBreakpoints';
import MDTypography from 'components/atoms/MDTypography/MDTypography';
import MDButton from 'components/atoms/MDButton/MDButton';
import colors from 'assets/theme/base/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faCircleNotch, faDownload, faPen, faX } from '@fortawesome/free-solid-svg-icons';
import { RouteKeys } from 'models/RouteModels';
import { usePatientData } from 'hooks/usePatientData';
import { routesMap } from 'data/routes';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Appointment, KneeViewOverride, TriageXrayStatus } from 'models/AppointmentModels';
import { Patient } from 'models/PatientModels';
import Spinner from 'components/molecules/Spinner/Spinner';
import { User } from 'models/UserModels';
import XrayThumb from './XrayThumb';
import TriageFlowLayout from 'components/templates/TriageFlowLayout/TriageFlowLayout';
import { useDropzone } from 'react-dropzone';
import EditJointsForm from './EditJointsForm';
import Alert from 'components/atoms/Alert/Alert';

import { ReactComponent as DownloadIcon } from '../../../assets/images/icons/download.svg';
import { ProductVersion } from 'models/OrgModels';
import { useAppointmentMutations } from 'hooks/useAppointmentMutations';
import Switch from 'components/atoms/Switch/Switch';
import { AppointmentExams, useAppointmentExamsData } from 'hooks/useAppointmentExamsData';

import * as dicomParser from 'dicom-parser';

interface IMatchXraysPageProps {
    user: User;
    accessToken: string;
    appointment: Appointment;
    singlePatient: Patient;
    allAppointmentXrays: Xray[];
    appointmentExams: AppointmentExams;
    refetchAppointmentExams: () => void;
    flow: ProductVersion;
}

const MatchXraysContent: React.FC<IMatchXraysPageProps> = ({
    user,
    accessToken,
    appointment,
    singlePatient,
    allAppointmentXrays,
    appointmentExams,
    refetchAppointmentExams,
    flow
}) => {
    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [draggedOverUnassigned, setDraggedOverUnassigned] = useState<boolean>(false);
    const [showConfirmMissingXraysModal, setShowConfirmMissingXraysModal] = useState(false);
    const [showEditJointsModal, setShowEditJointsModal] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const navigate = useNavigate();
    const { breakpointBreached: mdBreakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.MEDIUM });
    const { breakpointBreached: lgBreakpointBreached } = useBreakpoints({ breakpoint: Breakpoints.LARGE });

    const {
        updateXray,
        updateXrayLoading,
        createBatchXrayLoading,
        createBatchXrays,
        autoAssignXrays,
        autoAssignXraysLoading
    } = useXrayMutations({ appointment });

    const isAnyRequestLoading = createBatchXrayLoading || autoAssignXraysLoading || updateXrayLoading;
    const { refetchAllAppointmentXrays } = useXrayData({ appointment });
    const { setRequiredViewOptionsMutation, updateAppointment } = useAppointmentMutations()

    const {
        getRootProps,
        getInputProps,
    } = useDropzone({
        disabled: isAnyRequestLoading,
        onDropAccepted: async (acceptedFiles) => {
            setDraggedOverUnassigned(false);

            const validFiles = [];

            for (const file of acceptedFiles) {

                if (file.type === 'application/zip') {
                    validFiles.push(file);

                    continue;
                }

                if (file.type === 'image/jpeg') {
                    validFiles.push(file);

                    continue;
                }

                try {
                    const bytes = await file.arrayBuffer();
                    const byteArray = new Uint8Array(bytes);

                    const dicomData = dicomParser.parseDicom(
                        byteArray,
                        {
                            TransferSyntaxUID: '1.2.840.10008.1.2'
                        }
                    );

                    const pixelDataElement = dicomData.elements.x7fe00010;

                    if (pixelDataElement !== undefined) {
                        validFiles.push(file);
                    }

                } catch (error) {
                    console.log(file.name, " is not a DICOM file");
                }
            }

            createBatchXrays({
                files: validFiles,
                patientId: singlePatient.patient_id
            }, {
                onError: () => {
                    setErrorMessage('Failed to upload X-ray files. Please try again.');
                    setShowErrorAlert(true);
                },
                onSuccess: () => {
                    autoAssignXrays({
                        appointmentId: appointment.appointment_id,
                        appointmentDate: appointment.appointment_date
                    },
                        {
                            onError: () => {
                                setErrorMessage('Failed to automatically assign X-rays. Please try again.');
                                setShowErrorAlert(true);
                            },
                            onSuccess: () => {
                                refetchAllAppointmentXrays()
                            }
                        }
                    );
                }
            });
        }
    });

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, id: string) => {
        if (isAnyRequestLoading) {
            e.preventDefault();
            return;
        }
        setDraggedItem(id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOverUnassigned = (e: React.DragEvent<HTMLDivElement>) => {
        if (isAnyRequestLoading) {
            e.preventDefault();
            return;
        }
        e.preventDefault();
        setDraggedOverUnassigned(true)

        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragLeaveUnassigned = (e: React.DragEvent<HTMLDivElement>) => {
        if (isAnyRequestLoading) {
            e.preventDefault();
            return;
        }
        e.preventDefault();
        setDraggedOverUnassigned(false)
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>, viewType: ViewTypes | null, examType: ExamTypes | undefined | null) => {
        if (isAnyRequestLoading) {
            e.preventDefault();
            return;
        }

        e.preventDefault();

        if (draggedItem === null) return;

        const xray = allAppointmentXrays?.find((xray) => xray.xray_id === draggedItem)!

        setDraggedOverUnassigned(false)

        await updateXray(
            {
                xray,
                newValues: {
                    client_provided_view_type: viewType,
                    joint: examType,
                }
            },
            {
                onError: (error) => {
                    console.log('updateXray error:', error);
                    setErrorMessage('Failed to update X-ray. Please try again.');
                    setShowErrorAlert(true);
                }
            })

        setDraggedItem(null);
    };

    const handleSubmission = async () => {
        try {
            let xrays: Xray[] = []
            examItems.forEach((examItem) => {
                xrays = [...xrays, ...(Object.values(examItem.matchedXrays).map(xray => xray))]
            })

            const xraysForAnalysis = xrays
                .reduce((prev, curr) => {
                    const key = curr.client_provided_view_type
                    if (!key) {
                        return prev
                    }

                    return {
                        ...prev,
                        [key]: curr
                    }
                }, {})

            // AVT: this is a temporary fix to the issue where the xray status is not being updated to "ANALYZING"
            // TODO: remove this once the issue is fixed by having the endpoint kickoff analysis and return, ensuring the status is updated
            await updateAppointment({
                appointmentId: appointment.appointment_id,
                appointmentDate: appointment.appointment_date,
                newValues: {
                    xray_status: TriageXrayStatus.ANALYZING
                }
            });

            // Then send xrays for analysis (fire and forget)
            sendXraysForAnalysis(xraysForAnalysis, accessToken, user)
        } catch (error) {
            console.error('Error in handleSubmission:', error);
            setErrorMessage('Failed to submit X-rays. Please try again.');
            setShowErrorAlert(true);
            throw error; // Re-throw to prevent navigation
        }
    }

    const activeExamTypes = getActiveExamTypesFromAppointment(appointment);

    const isViewTypeInRequiredViews = (viewType: ViewTypes, specificExamType?: ExamTypes) => {
        if (specificExamType) {
            const requiredViews = appointmentExams[specificExamType]?.required_views;
            return requiredViews && Object.values(requiredViews).some(
                (reqView: any) => reqView.key === viewType
            );
        }

        return activeExamTypes.some(examType => {
            const requiredViews = appointmentExams[examType]?.required_views;
            return requiredViews && Object.values(requiredViews).some(
                (reqView: any) => reqView.key === viewType
            );
        });
    };

    const examItems = activeExamTypes.map((examType: ExamTypes) => {
        const requiredViews = appointmentExams[examType]?.required_views;

        const matchedXrays: { [key: string]: any } = (allAppointmentXrays || [])
            .filter((xray) => {
                return xray.client_provided_view_type &&
                    xray.joint === examType &&
                    isViewTypeInRequiredViews(xray.client_provided_view_type, examType);
            })
            .reduce((prev, curr) => {
                return { ...prev, [curr.client_provided_view_type]: curr }
            }, {});

        return {
            id: examType,
            matchedXrays,
            requiredViews
        }
    });

    const unmatchedXrays = allAppointmentXrays?.filter((xray) => {
        if (!xray.client_provided_view_type) {
            return true;
        }

        return !isViewTypeInRequiredViews(xray.client_provided_view_type);
    });

    const flowRoute = flow === ProductVersion.TRIAGE ? routesMap[RouteKeys.TRIAGE_LIST].route : `${routesMap[RouteKeys.APPOINTMENT_OVERVIEW].baseRoute}/${appointment.patient_id}?date=${appointment.appointment_date}`

    const hasUnmatchedXrays = unmatchedXrays.length > 0
    const isBilateral = (examType: ExamTypes, kneeViewOverride: KneeViewOverride | undefined) => {
        if (examType === ExamTypes.BILAT_KNEE) {
            return !kneeViewOverride || kneeViewOverride === KneeViewOverride.BILATERAL
        }

        if ((examType === ExamTypes.SINGLE_KNEE_RIGHT || examType === ExamTypes.SINGLE_KNEE_LEFT)) {
            return kneeViewOverride && kneeViewOverride === KneeViewOverride.BILATERAL
        }

        return true
    }

    const uploadInstructions = (
        <span style={{ textAlign: 'center', display: 'block', padding: ".5rem", borderRadius: "6px", color: colors.secondary.main, fontWeight: 700, backgroundColor: "#E4EEF7" }}>
            Drag X-rays from the 'Upload' box to the 'Confirmation' box or vice versa to switch out X-rays.
        </span>
    )

    return (
        <Container>
            <TriageFlowLayout title="Upload X-rays" singlePatient={singlePatient}>
                <MDBox
                    display="grid"
                    gridTemplateColumns={lgBreakpointBreached ? "1fr" : "1fr 5fr"}
                    alignItems="center"
                    style={{ position: 'relative' }}
                    padding="2rem 0"
                    paddingBottom=".5rem"
                    gap="2rem">

                    <MDButton
                        onClick={() => navigate(flowRoute)}
                        variant="outlined" style={{
                            color: colors.secondary.main,
                            borderColor: colors.secondary.main,
                            whiteSpace: 'nowrap',
                            width: 'fit-content',
                        }}>
                        <FontAwesomeIcon icon={faChevronLeft} color={colors.secondary.main} style={{ marginRight: '.5rem' }} />
                        {flow === ProductVersion.FULL ? "Patient Overview" : "Triage List"}
                    </MDButton>

                    {!lgBreakpointBreached && allAppointmentXrays.length > 0 ? uploadInstructions : <></>}

                </MDBox>

                <MDBox pt={2}>
                    <MDBox display="grid" gridTemplateColumns={lgBreakpointBreached ? "1fr" : "1fr 1fr"} gap={lgBreakpointBreached ? "0rem" : "2rem"}>
                        <MDBox display="grid" gridTemplateRows="3rem 50vh" gap="1rem" pb="1rem">
                            <MDBox display="flex" justifyContent="space-between" alignItems='flex-end' height="3rem">
                                <Typography color={MUIColors.SECONDARY} fontWeight="bold">
                                    Unassigned X-Rays
                                </Typography>
                                <MDButton
                                    {...getRootProps()}
                                    color={MUIColors.SECONDARY}
                                    variant="contained"
                                >
                                    <FontAwesomeIcon
                                        spin={createBatchXrayLoading}
                                        icon={createBatchXrayLoading ? faCircleNotch : faDownload}
                                        color={colors.white.main}
                                        style={{ marginRight: '.5rem' }} />
                                    {createBatchXrayLoading ? 'Uploading...' : 'Upload More'}
                                </MDButton>
                            </MDBox>

                            <MDBox
                                border="4px dashed"
                                borderRadius="10px"
                                padding='2rem'
                                alignItems="flex"
                                height={"100%"}
                                style={{
                                    overflowY: 'auto',
                                    opacity: isAnyRequestLoading ? 0.6 : 1,
                                    pointerEvents: isAnyRequestLoading ? 'none' : 'auto'
                                }}
                                borderColor={draggedOverUnassigned ? colors.success.main : colors.secondary.main}

                                onDrop={(e) => handleDrop(e, null, null)}
                                {...(draggedOverUnassigned && draggedItem === null ? getRootProps() : {})}
                                onDragOver={(e) => handleDragOverUnassigned(e)}
                                onDragLeave={(e) => handleDragLeaveUnassigned(e)}
                            >

                                {!createBatchXrayLoading && draggedOverUnassigned && <input {...getInputProps()} />}

                                <MDBox
                                    display="inline-flex"
                                    textAlign="center"
                                    justifyContent="center"
                                    borderRadius="6px"
                                    alignItems="center"
                                    padding=".5rem"
                                    mb="2rem"
                                    bgcolor={"#E4EEF7"}>
                                    <Typography
                                        color={MUIColors.SECONDARY}
                                        fontSize="14px"
                                        fontWeight="bold">
                                        Reminder: You MUST ANONYMIZE any information you upload to Joint Ai.
                                    </Typography>
                                </MDBox>

                                {(createBatchXrayLoading || autoAssignXraysLoading)
                                    ? (<MDBox
                                        display="grid"
                                        justifyContent="center"
                                        top='.8rem'
                                        left='1rem'
                                        height="100%"
                                        gridAutoRows="max-content">
                                        <Spinner />
                                        <Typography variant="h4" style={{ marginTop: '10px', color: colors.secondary.main }}>Processing...</Typography>
                                    </MDBox>)
                                    : (hasUnmatchedXrays)
                                        ? (<MDBox
                                            height="100%"
                                            display="grid"
                                            gridAutoRows="max-content"
                                            gridTemplateColumns="1fr 1fr 1fr" gap="1.5rem"
                                        >
                                            {unmatchedXrays?.map((xray) => (
                                                <XrayThumb
                                                    key={xray.xray_id}
                                                    appointment={appointment}
                                                    xray={xray}
                                                    onDragStart={handleDragStart}
                                                />
                                            ))}
                                        </MDBox>)
                                        : (<MDBox
                                            display="flex"
                                            justifyContent="center"
                                            gridAutoRows="max-content"
                                            gridTemplateColumns="1fr 1fr 1fr" gap="1.5rem">
                                            <div>
                                                <MDBox
                                                    display="grid"
                                                    justifyContent="center"
                                                    gap="1rem"
                                                    padding="1rem">

                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        <DownloadIcon />
                                                    </div>

                                                    <MDButton
                                                        variant='contained'
                                                        size='small'
                                                        color={MUIBaseColors.SECONDARY}
                                                    >
                                                        Browse
                                                    </MDButton>

                                                    <MDTypography textAlign="center" fontSize="10px" sx={{ color: colors.primary }}>
                                                        or drag file to upload
                                                    </MDTypography>

                                                    <MDTypography textAlign="center" fontSize="10px" sx={{ color: colors.grey[600] }}>
                                                        <span style={{ color: 'red' }}>*</span>File types supported: .jpeg, .jpg
                                                    </MDTypography>

                                                </MDBox>
                                            </div>
                                        </MDBox>)
                                }
                            </MDBox>
                        </MDBox>

                        {lgBreakpointBreached && allAppointmentXrays.length > 0 ? uploadInstructions : <></>}

                        <MDBox display="grid" gridTemplateRows="3rem 50vh" gap="1rem">
                            <MDBox display="flex" justifyContent="space-between" alignItems='flex-end'>
                                <Typography color={MUIColors.SECONDARY} fontWeight="bold">
                                    X-Rays for Evaluation
                                </Typography>

                                <MDButton
                                    color={MUIColors.SUCCESS}
                                    variant="contained"
                                    onClick={async () => {
                                        const totalMissingXrays = examItems.flatMap((examItem) => {
                                            const matchedXRayViewTypes = Object.values(examItem.matchedXrays).map((xray) => xray.client_provided_view_type)
                                            const requiredViews = Object
                                                .values(examItem.requiredViews || {})
                                                .filter((reqView) => !reqView.isOptional)
                                                .map((reqView) => reqView.key)

                                            return requiredViews.filter(reqView => {
                                                return !matchedXRayViewTypes.includes(reqView)
                                            });
                                        })

                                        if (totalMissingXrays.length > 0) {
                                            setShowConfirmMissingXraysModal(true)
                                            return
                                        }

                                        await handleSubmission()

                                        navigate(flowRoute)
                                    }}
                                >
                                    Submit X-rays
                                </MDButton>
                            </MDBox>

                            <MDBox
                                border="3px solid"
                                borderColor={colors.secondary.main}
                                borderRadius="10px"
                                padding="1rem"
                                style={{
                                    overflowY: 'scroll',
                                    opacity: isAnyRequestLoading ? 0.6 : 1,
                                    pointerEvents: isAnyRequestLoading ? 'none' : 'auto'
                                }}
                                display="grid"
                                gridAutoRows="max-content"
                                gap="2rem">

                                {examItems.map((item) => (

                                    <MDBox key={item.id} display="grid" gap="1rem">
                                        <Typography variant='h4' color={MUIColors.SECONDARY}>{ExamNameNormalizer[item.id]}</Typography>
                                        {
                                            [
                                                ExamTypes.BILAT_KNEE,
                                                ExamTypes.SINGLE_KNEE_LEFT,
                                                ExamTypes.SINGLE_KNEE_RIGHT,
                                            ].includes(item.id) ?
                                                (
                                                    <div>
                                                        <Switch
                                                            isOn={isBilateral(item.id, appointment.exam_view_options?.[item.id]?.knee_view_override)}
                                                            offLabel='Unilateral'
                                                            onLabel='Bilateral'
                                                            onClick={async () => {
                                                                await setRequiredViewOptionsMutation({
                                                                    appointmentId: appointment.appointment_id,
                                                                    appointmentDate: appointment.appointment_date,
                                                                    payload: {
                                                                        exam_type: item.id,
                                                                        knee_view_override: isBilateral(item.id, appointment.exam_view_options?.[item.id]?.knee_view_override) ? KneeViewOverride.UNILATERAL : KneeViewOverride.BILATERAL
                                                                    }
                                                                })

                                                                refetchAppointmentExams()
                                                            }}
                                                        />
                                                        <MDBox textAlign="center" paddingTop="1rem">
                                                            <Typography fontSize="14px">
                                                                *Note: If PA knee views provided are not weight bearing, degeneration may be worse than reported here
                                                            </Typography>
                                                        </MDBox>
                                                    </div>
                                                )
                                                : null
                                        }
                                        <MDBox display="grid" gridTemplateColumns="1fr 1fr 1fr" gap="1rem">

                                            {item.requiredViews && Object.values(item.requiredViews).map((reqView) => {
                                                const viewTypeValue = reqView.key
                                                const xray = item.matchedXrays[viewTypeValue]

                                                return (
                                                    <XrayThumb
                                                        key={`${reqView.key}_${item.id}`}
                                                        appointment={appointment}
                                                        viewType={reqView}
                                                        xray={xray}
                                                        exam={item.id}
                                                        onDrop={handleDrop}
                                                        onDragStart={handleDragStart}
                                                        loading={(updateXrayLoading && draggedItem === xray?.xray_id) || isAnyRequestLoading} />
                                                )
                                            })}
                                        </MDBox>
                                    </MDBox>))}

                                <MDBox display="flex" gap="1rem" justifyContent="flex-end">
                                    <MDButton
                                        startIcon={<FontAwesomeIcon icon={faPen} />}
                                        color={MUIColors.SECONDARY}
                                        variant="outlined"
                                        style={{ width: 'fit-content' }}
                                        onClick={async () => {
                                            setShowEditJointsModal(true)
                                        }}>

                                        Edit Joints
                                    </MDButton>
                                </MDBox>

                            </MDBox>
                        </MDBox>
                    </MDBox>
                </MDBox>
            </TriageFlowLayout >

            <Modal open={showEditJointsModal} onClose={() => setShowEditJointsModal(false)}>
                <MDBox {...containerStyles({ justifyContent: 'center', alignItems: 'center' })}>
                    <Card {...containerStyles({
                        width: mdBreakpointBreached ? '90vw' : '50%',
                        height: '50vh',
                        minHeight: '380px',
                        pt: '20px',
                        pb: '40px',
                        maxWidth: '50rem',
                        minWidth: '20rem',
                        padding: '20%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        position: 'relative',
                    })}>
                        <FontAwesomeIcon
                            icon={faX}
                            cursor='pointer'
                            onClick={() => setShowEditJointsModal(false)}
                            style={{ position: 'absolute', top: '2rem', right: '2rem', color: colors.secondary.main }}
                        />

                        <MDBox {...containerStyles({
                            flexDirection: 'row',
                            height: 'fit-content',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '2.5rem',
                            gap: '2rem'
                        })}>
                            <EditJointsForm appointment={appointment} />
                        </MDBox>
                    </Card>
                </MDBox>
            </Modal>

            <Modal open={showConfirmMissingXraysModal} onClose={() => setShowConfirmMissingXraysModal(false)}>
                <MDBox {...containerStyles({ justifyContent: 'center', alignItems: 'center' })}>
                    <Card {...containerStyles({
                        width: mdBreakpointBreached ? '90vw' : '50%',
                        height: '30vh',
                        maxWidth: '50rem',
                        minWidth: '20rem',
                        padding: '20%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        position: 'relative',
                    })}>
                        <FontAwesomeIcon
                            icon={faX}
                            cursor='pointer'
                            onClick={() => setShowConfirmMissingXraysModal(false)}
                            style={{ position: 'absolute', top: '1rem', right: '1rem', color: colors.secondary.main }}
                        />

                        <MDTypography sx={{ marginBottom: '2rem' }} variant='h3' fontWeight='regular' color={MUIColors.SECONDARY}>
                            WARNING: <span style={{ color: colors.error.focus }}>YOU ARE MISSING X-RAYS</span>
                        </MDTypography>
                        <MDTypography variant='h5' color={MUIColors.SECONDARY} fontWeight="regular">
                            This may result in an inaccurate diagnosis.
                        </MDTypography>
                        <MDTypography variant='h5' color={MUIColors.SECONDARY} fontWeight="regular">
                            Are you sure you want to proceed with the evaluation?
                        </MDTypography>
                        <MDBox {...containerStyles({
                            flexDirection: 'row',
                            height: 'fit-content',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: '2.5rem',
                            gap: '2rem'
                        })}>
                            <MDButton
                                color={MUIColors.SECONDARY}
                                variant='contained'
                                onClick={async () => {
                                    await handleSubmission()
                                    navigate(flowRoute)
                                }}>
                                Process X-rays
                            </MDButton>
                            <MDButton variant='contained' color={MUIColors.SECONDARY} onClick={() => navigate(flowRoute)}>Save for Later</MDButton>
                        </MDBox>
                    </Card>
                </MDBox>
            </Modal>

            <Alert
                show={showErrorAlert}
                type="error"
                handleClose={() => setShowErrorAlert(false)}
            >
                {errorMessage}
            </Alert>
        </Container>
    );
};

const MatchXraysFallback: React.FC = () => {
    return (
        <Container>
            <MDAppBar />
            <Grid item xs={12} pt={2}>
                <Card>
                    <MDBox
                        mx={1}
                        mt={-3}
                        py={2}
                        px={3}
                        bgColor={MUIColors.SECONDARY}
                        borderRadius="lg"
                        coloredShadow="secondary"
                        display="flex"
                        flexDirection='column'
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        height="auto"
                    >
                        <MDBox
                            top='.8rem'
                            left='1rem'
                        >
                            <MDTypography
                                sx={{ marginBottom: '0' }}
                                variant='h5'
                                color={MUIColors.WHITE}
                            >
                                ----- ------
                            </MDTypography>
                            <MDTypography
                                fontSize='1rem'
                                color={MUIColors.WHITE}
                            >
                                Patient ID: ------
                            </MDTypography>
                        </MDBox>

                        <MDTypography
                            variant="h5"
                            color={MUIColors.WHITE}
                        >
                            Upload X-rays
                        </MDTypography>



                    </MDBox>
                    <MDBox p={2} height="50vh">
                        <MDBox
                            height="50vh"
                            display="grid"
                            justifyContent="center"
                            top='.8rem'
                            left='1rem'
                            padding='3rem'
                            gap='1rem'
                        >
                            <Spinner />
                            <Typography variant="h4" style={{ marginTop: '10px', color: colors.secondary.main }}>Loading...</Typography>
                        </MDBox>
                    </MDBox>
                </Card>
            </Grid >
        </Container >
    )
}

const MatchXraysPage: React.FC = () => {
    const { accessToken, user } = useAuth();
    const { appointmentId } = useParams();
    const [searchParams] = useSearchParams();
    const flow = searchParams.get('flow') === ProductVersion.FULL ? ProductVersion.FULL : ProductVersion.TRIAGE;

    const {
        appointment,
        appointmentLoading,
    } = useAppointmentData({
        selectedDateISOString: searchParams.get('date'),
        appointmentId,
    });
    const {
        appointmentExams,
        appointmentExamsLoading,
        appointmentExamsError,
        refetchAppointmentExams
    } = useAppointmentExamsData({
        selectedDateISOString: searchParams.get('date'),
        appointmentId,
    });
    const requiredViewsReady = appointmentExams && !(appointmentExamsLoading || appointmentExamsError)

    const {
        singlePatient,
        singlePatientLoading,
    } = usePatientData({ patientId: appointment?.patient_id });

    const {
        allAppointmentXrays,
    } = useXrayData({ appointment });

    return (!(appointmentLoading || singlePatientLoading || appointmentLoading) &&
        appointment && singlePatient && allAppointmentXrays && appointmentId && user && accessToken && flow && requiredViewsReady

    ) ? <MatchXraysContent
        user={user}
        accessToken={accessToken}
        appointment={appointment}
        singlePatient={singlePatient}
        allAppointmentXrays={allAppointmentXrays}
        appointmentExams={appointmentExams}
        refetchAppointmentExams={refetchAppointmentExams}
        flow={flow}
    /> : <MatchXraysFallback />
}


export default MatchXraysPage;
